/**
 * KDS operator SPA — entry component.
 *
 * Phase C.1 of the unified-admin migration. Served by the admin-router
 * at admin.tovriktech.dev/m/{mid}/kds — the router strips the
 * /m/{mid}/kds prefix and forwards to this app's static bundle. Rest
 * of the path drives client-side routing.
 *
 * Current scope (MVP):
 *   /m/{mid}/kds           → DeviceListPage (with ?firstrun=1 empty state)
 *   /m/{mid}/kds/pair      → PairDevicePage
 *   /m/{mid}/kds/monitor   → LiveMonitorPage (placeholder)
 *
 * Deferred (needs new kds-api routes with session validation):
 *   • Live merchant device list (today shows empty state by default)
 *   • Rename / revoke actions
 *   • OTA channel display
 *
 * Until those land, the SPA can be exercised end-to-end in the
 * pair-new flow (which uses the public /api/v1/devices/pair-init +
 * pair-status endpoints).
 */

import { useState, useMemo, useEffect, type ReactNode } from 'react';
import { DeviceListPage } from './pages/DeviceListPage';
import { PairDevicePage } from './pages/PairDevicePage';
import { LiveMonitorPage } from './pages/LiveMonitorPage';
import { ReleasesPage } from './pages/ReleasesPage';
import { AuthGate } from './components/AuthGate';
import { resolveBasename, stripBasename } from './lib/basename';
import { resolveKdsApiBase } from './lib/api';

interface Route {
  kind: 'list' | 'pair' | 'monitor' | 'releases' | 'unknown';
  merchantId?: string;
  firstRun?: boolean;
  /** True when route comes from the production /m/{mid}/kds URL shape;
   *  false when we synthesized a demo route for `npm run dev`. AuthGate
   *  is mounted only for production routes — standalone dev bypasses the
   *  auth probe entirely so a developer can render component demos
   *  against the mer_demo merchant without bouncing to OIDC.
   *
   *  NO route-kind exemption. `releases` used to skip AuthGate because OTA
   *  admin carried its own pasted `kds_ota_admin_token` localStorage bearer
   *  (Sprint-4 F1) — that shared secret is being retired as a human authority
   *  (S5 kds-ota-human-auth-retire-shared-token), and the page now exchanges
   *  the operator's real session for a short-lived per-human token, so a
   *  session is REQUIRED. */
  isProduction?: boolean;
}

export function parseRoute(path: string, search: string): Route {
  const firstRun = search.includes('firstrun=1');
  // shell-mount-gate-allow: parseRoute owns staff-mount routing — this is the
  // SPA's hand-rolled router (no React Router); matchMount can't replace it
  // without a router rewrite (out of scope). It is the ONE sanctioned /m/ matcher.
  const m = path.match(/^\/m\/([^/]+)\/kds(\/.*)?$/);
  if (m) {
    const merchantId = m[1];
    const rest = m[2] ?? '/';
    if (rest.startsWith('/pair')) return { kind: 'pair', merchantId, firstRun, isProduction: true };
    if (rest.startsWith('/monitor')) return { kind: 'monitor', merchantId, firstRun, isProduction: true };
    if (rest.startsWith('/releases')) return { kind: 'releases', merchantId, firstRun, isProduction: true };
    return { kind: 'list', merchantId, firstRun, isProduction: true };
  }
  // Standalone dev — when not behind the router, hitting / still works
  // for component demos.
  if (path === '/' || path === '/pair' || path === '/releases') {
    if (path === '/pair') return { kind: 'pair', merchantId: 'mer_demo', firstRun, isProduction: false };
    if (path === '/releases') return { kind: 'releases', merchantId: 'mer_demo', firstRun, isProduction: false };
    return { kind: 'list', merchantId: 'mer_demo', firstRun, isProduction: false };
  }
  return { kind: 'unknown' };
}

export function App(): JSX.Element {
  const [path, setPath] = useState(() => window.location.pathname);
  const [search, setSearch] = useState(() => window.location.search);

  // Mount-agnostic basename (WS-PHASE2-SHELL Stage 1). When the Phase-2
  // merchant shell serves this SPA under a fixed `/kds` prefix, we
  // strip that prefix before route parsing and re-add it on navigation
  // so the same route table works at every mount. Resolved per-path so
  // a popstate that lands on a different prefix is handled too.
  const basename = useMemo(() => resolveBasename(path), [path]);

  // WS-PHASE2-SHELL Stage 5 (DL-13 follow-up): under the one-origin shell
  // mount (`/kds`) there is NO `/m/{mid}` in the URL, so parseRoute below
  // would fall back to the `mer_demo` dev placeholder. Instead resolve the
  // REAL merchant from the apex session via GET /api/v1/me/merchants (which
  // accepts the apex `msi_session`), mirroring HS's /api/me/shell-session.
  const isShell = basename !== '';
  const [shellMerchant, setShellMerchant] = useState<string | null>(null);
  const [shellResolved, setShellResolved] = useState(!isShell);
  useEffect(() => {
    if (!isShell) return;
    let cancelled = false;
    setShellResolved(false);
    fetch(`${resolveKdsApiBase(window.location.pathname)}/api/v1/me/merchants`, {
      credentials: 'include',
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { active_merchant_id?: string | null } | null) => {
        if (!cancelled) {
          setShellMerchant(d?.active_merchant_id ?? null);
          setShellResolved(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setShellMerchant(null);
          setShellResolved(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isShell]);

  useEffect(() => {
    function onPop() {
      setPath(window.location.pathname);
      setSearch(window.location.search);
    }
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  function navigate(newPath: string) {
    // newPath is an in-app absolute path (e.g. `/m/{mid}/kds/pair`).
    // Re-prefix the active basename so the browser URL stays under the
    // current mount (e.g. `/kds/m/{mid}/kds/pair`).
    const url = basename ? basename + newPath : newPath;
    window.history.pushState({}, '', url);
    setPath(url);
    setSearch(window.location.search);
  }

  const route = useMemo(
    () => parseRoute(stripBasename(path, basename), search),
    [path, search, basename],
  );

  if (route.kind === 'unknown') {
    return (
      <div className="p-10 text-center text-sm text-tvk-fg-muted">
        Not found.
      </div>
    );
  }

  // One-origin shell mount (`/kds`): render with the SESSION-resolved
  // merchant (not the mer_demo placeholder parseRoute produced) and treat
  // it as production (real auth via AuthGate over the apex session).
  if (isShell) {
    if (!shellResolved) {
      return <div className="p-10 text-center text-sm text-tvk-fg-muted">Loading…</div>;
    }
    if (!shellMerchant) {
      // No merchant on the apex session → the apex shell owns auth; defer
      // to its login rather than probing kds-api with a placeholder.
      if (typeof window !== 'undefined') window.location.href = '/login';
      return <div className="p-10 text-center text-sm text-tvk-fg-muted">Redirecting to sign in…</div>;
    }
    const shellPage = renderPage(route.kind, shellMerchant, route.firstRun ?? false, navigate);
    // Every shell route — including `releases` — goes through AuthGate.
    return <AuthGate merchantId={shellMerchant}>{shellPage}</AuthGate>;
  }

  const merchantId = route.merchantId!;
  const page = renderPage(route.kind, merchantId, route.firstRun ?? false, navigate);

  // Production routes (/m/{mid}/kds[/...]) require a session; AuthGate probes
  // on mount and bounces to OIDC sign-in on 401. Standalone dev routes (`/`,
  // `/pair`, `/releases`) skip the gate so `npm run dev` doesn't loop-redirect
  // when no cookie is present.
  //
  // `releases` is NO LONGER exempt in production (S5
  // kds-ota-human-auth-retire-shared-token): the page exchanges the operator's
  // own session for a short-lived per-human OTA token, so an unauthenticated
  // visitor must be sent to sign-in rather than shown a token-paste panel.
  if (route.isProduction) {
    return <AuthGate merchantId={merchantId}>{page}</AuthGate>;
  }
  return <>{page}</>;
}

function renderPage(
  kind: 'list' | 'pair' | 'monitor' | 'releases',
  merchantId: string,
  firstRun: boolean,
  navigate: (path: string) => void,
): ReactNode {
  switch (kind) {
    case 'list':
      return (
        <DeviceListPage
          merchantId={merchantId}
          firstRun={firstRun}
          onPairNew={() => navigate(`/m/${merchantId}/kds/pair`)}
          onOpenMonitor={() => navigate(`/m/${merchantId}/kds/monitor`)}
        />
      );
    case 'pair':
      return (
        <PairDevicePage
          merchantId={merchantId}
          onDone={() => navigate(`/m/${merchantId}/kds`)}
          onCancel={() => navigate(`/m/${merchantId}/kds`)}
        />
      );
    case 'monitor':
      return (
        <LiveMonitorPage
          merchantId={merchantId}
          onBack={() => navigate(`/m/${merchantId}/kds`)}
        />
      );
    case 'releases':
      return <ReleasesPage />;
  }
}
